import { Mail, Trash2, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/common/empty-state";
import { SkeletonRow } from "@/components/common/skeleton-row";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreateInvitationButton } from "@/features/members/components/CreateInvitationButton";
import { RevokeInvitationDialog } from "@/features/members/components/DeleteInvitationDialog";
import { useInvitations } from "@/features/members/hooks/useInvitations";
import { useMembers } from "@/features/members/hooks/useMembers";

export const MemberList = () => {
  const {
    data: members,
    isLoading: isLoadingMembers,
    error: membersError,
    isError: isMembersError,
  } = useMembers();
  const {
    data: invitations,
    isLoading: isLoadingInvitations,
    error: invitationsError,
    isError: isInvitationsError,
  } = useInvitations();

  const [revokeInvitationId, setRevokeInvitationId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!isLoadingMembers && isMembersError) {
      toast.error("Failed to load members", {
        description:
          membersError instanceof Error
            ? membersError.message
            : "Please try again or contact support",
      });
    }
  }, [isLoadingMembers, isMembersError, membersError]);

  useEffect(() => {
    if (!isLoadingInvitations && isInvitationsError) {
      toast.error("Failed to load invitations", {
        description:
          invitationsError instanceof Error
            ? invitationsError.message
            : "Please try again or contact support",
      });
    }
  }, [isLoadingInvitations, isInvitationsError, invitationsError]);

  return (
    <div className="dashboard-page">
      <div className="page-heading">
        <div>
          <h1 className="page-title">Members</h1>
          <p className="mt-2.5 text-sm text-muted-foreground">
            Manage access to your organization.
          </p>
        </div>
        <div className="shrink-0">
          <CreateInvitationButton />
        </div>
      </div>

      <Card>
        <CardHeader className="border-b">
          <CardTitle>Active Members</CardTitle>
        </CardHeader>
        <CardContent className="px-6">
          {isLoadingMembers ? (
            <SkeletonRow count={2} />
          ) : members?.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No active members"
              description="Invite team members to collaborate with you."
            />
          ) : (
            <div className="divide-y divide-border">
              {members?.map((member) => (
                <div
                  key={member.id}
                  className="flex min-h-16 items-center gap-3 py-3"
                >
                  <span className="min-w-0 flex-1 text-sm text-foreground">
                    <span className="block truncate">{member.email}</span>
                  </span>
                  <Badge variant="secondary" className="capitalize shrink-0">
                    {member.role}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader className="border-b">
          <CardTitle>Pending Invitations</CardTitle>
        </CardHeader>
        <CardContent className="px-6">
          {isLoadingInvitations ? (
            <SkeletonRow count={2} />
          ) : invitations?.length === 0 ? (
            <EmptyState
              icon={Mail}
              title="No pending invitations"
              description="Invite team members to collaborate with you."
            />
          ) : (
            <div className="divide-y divide-border">
              {invitations?.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex min-h-16 items-center gap-3 py-3"
                >
                  <span className="min-w-0 flex-1 text-sm text-foreground">
                    <span className="block truncate">{invitation.email}</span>
                  </span>
                  <Badge variant="secondary" className="capitalize shrink-0">
                    {invitation.state}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Revoke invitation"
                    className="size-11 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setRevokeInvitationId(invitation.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <RevokeInvitationDialog
        invitationId={revokeInvitationId}
        onClose={() => setRevokeInvitationId(null)}
      />
    </div>
  );
};
